import React, { useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Mail, Phone, MapPin, Send, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { companyInfo } from '../data/mock';

export const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
    toast.success('Thank you! We will get back to you shortly.');
    setFormData({
      name: '',
      email: '',
      phone: '',
      company: '',
      message: ''
    });
  };

  const contactInfo = [
    {
      icon: Phone,
      title: 'Phone',
      detail: companyInfo.phone.join(', '),
      link: `tel:${companyInfo.phone[0]}`
    },
    {
      icon: Mail,
      title: 'Email',
      detail: companyInfo.email,
      link: `mailto:${companyInfo.email}`
    },
    {
      icon: MapPin,
      title: 'Location',
      detail: companyInfo.address,
      link: null
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Page Header */}
      <section className="relative pt-32 pb-16 bg-gradient-to-br from-slate-50 via-orange-50/30 to-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-4">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-orange-100 rounded-full mb-4">
              <Mail size={40} className="text-[#D8461D]" />
            </div>
            <h1 className="text-4xl lg:text-6xl font-bold text-slate-900">
              Get In Touch
            </h1>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              Have a question or need a custom solution? We're here to help.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Contact Info Sidebar */}
            <div className="lg:col-span-1 space-y-6">
              {/* Contact Cards */}
              {contactInfo.map((info, index) => {
                const IconComponent = info.icon;
                return (
                  <Card key={index} className="border-slate-200 hover:shadow-lg transition-shadow bg-white">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <IconComponent size={24} className="text-[#D8461D]" />
                        </div>
                        <div className="space-y-1">
                          <h4 className="font-bold text-slate-900">{info.title}</h4>
                          {info.link ? (
                            <a
                              href={info.link}
                              className="text-slate-600 hover:text-[#D8461D] transition-colors block"
                            >
                              {info.detail}
                            </a>
                          ) : (
                            <p className="text-slate-600">{info.detail}</p>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}

              {/* Business Hours */}
              <Card className="border-slate-200 bg-orange-50">
                <CardContent className="p-6 space-y-4">
                  <div className="flex items-center gap-3">
                    <Clock size={24} className="text-[#D8461D]" />
                    <h4 className="font-bold text-slate-900">Business Hours</h4>
                  </div>
                  <div className="space-y-2 text-sm text-slate-700">
                    <div className="flex justify-between">
                      <span>Tuesday - Sunday</span>
                      <span className="font-semibold">9:00 AM - 6:00 PM</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Monday</span>
                      <span className="font-semibold">Closed</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Quick Info */}
              <Card className="border-orange-200 bg-white">
                <CardContent className="p-6 space-y-3">
                  <h4 className="font-bold text-slate-900">Quick Response</h4>
                  <p className="text-sm text-slate-600">
                    We typically respond to all inquiries within 24 hours during business days.
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <Card className="border-slate-200 shadow-xl bg-white">
                <CardContent className="p-8">
                  <div className="mb-6">
                    <h2 className="text-2xl font-bold text-slate-900 mb-2">Send Us a Message</h2>
                    <p className="text-slate-600">Fill out the form below and we'll get back to you as soon as possible</p>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid sm:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <label htmlFor="name" className="text-sm font-semibold text-slate-700">
                          Full Name *
                        </label>
                        <Input
                          id="name"
                          name="name"
                          type="text"
                          required
                          value={formData.name}
                          onChange={handleChange}
                          placeholder="John Doe"
                          className="border-slate-300 focus:border-[#D8461D]"
                        />
                      </div>
                      <div className="space-y-2">
                        <label htmlFor="email" className="text-sm font-semibold text-slate-700">
                          Email Address *
                        </label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          required
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="john@company.com"
                          className="border-slate-300 focus:border-[#D8461D]"
                        />
                      </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <label htmlFor="phone" className="text-sm font-semibold text-slate-700">
                          Phone Number
                        </label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="+1 (555) 123-4567"
                          className="border-slate-300 focus:border-[#D8461D]"
                        />
                      </div>
                      <div className="space-y-2">
                        <label htmlFor="company" className="text-sm font-semibold text-slate-700">
                          Company Name
                        </label>
                        <Input
                          id="company"
                          name="company"
                          type="text"
                          value={formData.company}
                          onChange={handleChange}
                          placeholder="Your Company"
                          className="border-slate-300 focus:border-[#D8461D]"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label htmlFor="message" className="text-sm font-semibold text-slate-700">
                        Message *
                      </label>
                      <Textarea
                        id="message"
                        name="message"
                        required
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="Tell us about your requirements..."
                        rows={6}
                        className="border-slate-300 focus:border-[#D8461D] resize-none"
                      />
                    </div>

                    <Button
                      type="submit"
                      size="lg"
                      className="w-full bg-[#D8461D] hover:bg-#b83817 text-white font-semibold transition-all duration-200 hover:shadow-lg group"
                    >
                      <Send size={20} className="mr-2 group-hover:translate-x-1 transition-transform" />
                      Send Message
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Additional Info Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <h3 className="text-3xl font-bold text-slate-900">
              Looking for Bulk Orders or Custom Solutions?
            </h3>
            <p className="text-lg text-slate-600">
              Our sales team is ready to discuss volume pricing, custom manufacturing, 
              and specialized requirements for your business.
            </p>
            <div className="bg-orange-50 rounded-xl p-6 border border-orange-100">
              <p className="text-slate-700">
                <span className="font-semibold">For immediate assistance:</span> Call us at{' '}
                <a href={`tel:${companyInfo.phone[0]}`} className="text-[#D8461D] font-bold hover:underline">
                  {companyInfo.phone[0]}
                </a>
                {' or '}
                <a href={`tel:${companyInfo.phone[1]}`} className="text-[#D8461D] font-bold hover:underline">
                  {companyInfo.phone[1]}
                </a>
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
